import React, { useState } from 'react';
import {
  DataGrid,
  GridActionsCellItem,
  GridRowModes,
} from '@mui/x-data-grid';
import {
  Box,
  IconButton,
  TextField,
  Typography,
  Checkbox,
  Button,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Save,
  Cancel,
  CalendarToday,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { CustomPagination } from '../../../shared/Helper';
import settingsAtom from '../../../atoms/settings/SettingsAtom';
import { useAtom } from 'jotai';
import { isReadOnly } from '../../../utils/permissionUtils';

const initialColumnsConfig = [
  { key: 1, value: 'date', display_name: 'Date', type: 'date', format: 'DD-MM-YYYY AM', width: 200 },
  { key: 2, value: 'pilling', display_name: 'Pilling', type: 'int', format: 'Number', width: 150 },
  { key: 4, value: 'linked_part', display_name: 'Linked Part', type: 'int', format: 'Number', width: 150 },
  { key: 5, value: 'total_quantity', display_name: 'Total Quantity', type: 'int', format: 'Number', width: 180 },
  { key: 6, value: 'descrition', display_name: 'Description', type: 'text', format: 'string', width: 250 }
];

const EditableDataGrid = () => {
  const [rows, setRows] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [idCounter, setIdCounter] = useState(0);
  const [selectedRows, setSelectedRows] = useState(new Set()); // Track selected rows
  const [isSaving, setIsSaving] = useState(false); 
  const [settingsData] = useAtom(settingsAtom);
  const { projectList } = settingsData;
  const currentProject = projectList.find(o => o.id === parseInt(localStorage.getItem("project_id")));
  const timezone = currentProject?.timezone || 'Pacific/Auckland';

  const handleAddRow = () => {
    const id = idCounter + 1;
    const newRow = {
      id,
      date: dayjs().tz(timezone),
      pilling: '',
      linked_part: '',
      total_quantity: '',
      descrition: '',
      title: '',
      time: '',
      isNew: true,
    };
    setRows(prev => [...prev, newRow]);
    // Keep all existing row modes and add the new row in edit mode
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: 'date' },
    }));
    setIdCounter(id);
  };

  const handleEditClick = (id) => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => {
    // Get the current row data
    const currentRow = rows.find(row => row.id === id);
    if (!currentRow) return;

    // Validate the row before saving
    if (!currentRow.date || !dayjs(currentRow.date).isValid()) {
      alert(`Row ${id}: Valid date is required`);
      return;
    }
    
    if (currentRow.pilling !== '' && (isNaN(Number(currentRow.pilling)) || Number(currentRow.pilling) < 0)) {
      alert(`Row ${id}: Pilling must be a valid positive number`);
      return;
    }
    
    if (currentRow.linked_part !== '' && (isNaN(Number(currentRow.linked_part)) || Number(currentRow.linked_part) < 0)) {
      alert(`Row ${id}: Linked Part must be a valid positive number`);
      return;
    }
    
    if (currentRow.total_quantity !== '' && (isNaN(Number(currentRow.total_quantity)) || Number(currentRow.total_quantity) < 0)) {
      alert(`Row ${id}: Total Quantity must be a valid positive number`);
      return;
    }

    // Set saving flag and process the update
    setIsSaving(true);
    
    // Process the row update manually
    const updatedRow = { ...currentRow, isNew: false };
    setRows(rows.map((row) => (row.id === id ? updatedRow : row)));
    
    // Set row to view mode
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    
    // Reset saving flag
    setTimeout(() => setIsSaving(false), 100);
  };

  const handleDeleteClick = (id) => {
    setRows(rows.filter((row) => row.id !== id));
    // Remove from selected rows if it was selected
    setSelectedRows(prev => {
      const newSelected = new Set(prev);
      newSelected.delete(id);
      return newSelected;
    });
  };

  const handleCancelClick = (id) => {
    const isNew = rows.find((row) => row.id === id)?.isNew;
    if (isNew) {
      setRows(rows.filter((row) => row.id !== id));
      // Remove from selected rows if it was selected
      setSelectedRows(prev => {
        const newSelected = new Set(prev);
        newSelected.delete(id);
        return newSelected;
      });
    }
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const processRowUpdate = (newRow) => {
    // Only process if we're intentionally saving
    if (isSaving) {
      const updatedRow = { ...newRow, isNew: false };
      setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
      return updatedRow;
    }
    // Otherwise, return the original row to prevent automatic updates
    return rows.find(row => row.id === newRow.id) || newRow;
  };

  // Prevent automatic row updates when clicking outside, pressing Enter, etc.
  const handleRowEditStop = (params, event) => {
    // Prevent automatic saves unless we're intentionally saving
    if (!isSaving) {
      event.defaultMuiPrevented = true;
    }
  };

  // Get rows that are currently being edited or are new
  const getUnsavedRows = () => {
    return rows.filter(row => 
      row.isNew || 
      rowModesModel[row.id]?.mode === GridRowModes.Edit
    );
  };

  // Handle save all functionality
  const handleSaveAll = () => {
    const unsavedRows = getUnsavedRows();
    
    // Validate all unsaved rows
    for (const row of unsavedRows) {
      const isValidDate = dayjs.tz(row.date, timezone).isValid();
      if (!row.date || !isValidDate) {
        alert(`Row ${row.id}: Valid date is required`);
        return;
      }
      
      if (row.pilling !== '' && (isNaN(Number(row.pilling)) || Number(row.pilling) < 0)) {
        alert(`Row ${row.id}: Pilling must be a valid positive number`);
        return;
      }
      
      if (row.linked_part !== '' && (isNaN(Number(row.linked_part)) || Number(row.linked_part) < 0)) {
        alert(`Row ${row.id}: Linked Part must be a valid positive number`);
        return;
      }
      
      if (row.total_quantity !== '' && (isNaN(Number(row.total_quantity)) || Number(row.total_quantity) < 0)) {
        alert(`Row ${row.id}: Total Quantity must be a valid positive number`);
        return;
      }
    }

    // Set saving flag
    setIsSaving(true);

    // Save all unsaved rows
    const updatedRowModesModel = { ...rowModesModel };
    const updatedRows = rows.map(row => {
      if (row.isNew || rowModesModel[row.id]?.mode === GridRowModes.Edit) {
        // Set to view mode
        updatedRowModesModel[row.id] = { mode: GridRowModes.View };
        // Mark as no longer new
        return { ...row, isNew: false };
      }
      return row;
    });

    setRows(updatedRows);
    setRowModesModel(updatedRowModesModel);
    
    // Reset saving flag
    setTimeout(() => setIsSaving(false), 100);
    
    // Show success message
    alert(`Successfully saved ${unsavedRows.length} row(s)`);
  };

  // Check if there are unsaved rows
  const hasUnsavedRows = getUnsavedRows().length > 0;

  // Handle individual checkbox selection
  const handleRowSelection = (id, checked) => {
    setSelectedRows(prev => {
      const newSelected = new Set(prev);
      if (checked) {
        newSelected.add(id);
      } else {
        newSelected.delete(id);
      }
      return newSelected;
    });
  };

  // Handle header checkbox (select all/deselect all)
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRows(new Set(rows.map(row => row.id)));
    } else {
      setSelectedRows(new Set());
    }
  };

  // Check if all rows are selected
  const isAllSelected = rows.length > 0 && selectedRows.size === rows.length;
  const isIndeterminate = selectedRows.size > 0 && selectedRows.size < rows.length;

  const columns = [
    // Checkbox column as the first column
    {
      field: 'checkbox',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      resizable: false,
      pinned: 'left',
      renderHeader: () => (
        <Checkbox
          checked={isAllSelected}
          indeterminate={isIndeterminate}
          onChange={(e) => handleSelectAll(e.target.checked)}
          size="small"
          sx={{ padding: 0, color: "white" }}
        />
      ),
      renderCell: (params) => (
        <Checkbox
          checked={selectedRows.has(params.id)}
          onChange={(e) => handleRowSelection(params.id, e.target.checked)}
          size="small"
          sx={{ padding: 0 }}
        />
      ),
    },
    ...initialColumnsConfig.map((col) => ({
      field: col.value,
      headerName: col.display_name,
      width: col.width,
      minWidth: col.width - 50,
      editable: true,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      resizable: true,
      // Pin the date column to the left (after checkbox)
      ...(col.value === 'date' && { 
        pinned: 'left',
      }),
      flex: 1,
      renderEditCell: (params) => {
        const { id, field, value } = params;
        if (col.type === 'date') {
            return (
              <DatePicker
                value={dayjs(value).tz(timezone)}
                onChange={(newValue) =>
                    params.api.setEditCellValue({ id, field, value: newValue })
                }
                slots={{
                  openPickerIcon: () => (
                    <CalendarToday sx={{ fontSize: 16 }} />
                  ),
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    InputProps: {
                      sx: { fontSize: 16, height: 45, },
                    },
                    InputLabelProps: {
                      sx: { fontSize: 16 },
                    },
                  },
                }}
              />
            );
          }

        return (
          <TextField
            type={col.type === 'int' ? 'number' : 'text'}
            value={value}
            onChange={(e) =>
              params.api.setEditCellValue({ id, field, value: e.target.value })
            }
            fullWidth
            size="small"
            InputProps={{
                sx: {
                  height: 45,
                  fontSize: 16,
                  '& input': {
                    padding: '10px 14px',
                  },
                },
              }}
          />
        );
      },
      valueFormatter: (params) => {
        if (col.type === 'date') return dayjs(params.value).tz(timezone).format('DD-MM-YYYY');;
        return params.value;
      },
    })),
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      minWidth: 120,
      pinned: 'right',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem icon={<Save />} label="Save" onClick={() => handleSaveClick(id)} disabled ={isReadOnly()} />,
            <GridActionsCellItem icon={<Cancel />} label="Cancel" onClick={() => handleCancelClick(id)}  disabled ={isReadOnly()}/>,
          ];
        }

        return [
          <GridActionsCellItem icon={<Edit />} label="Edit" onClick={() => handleEditClick(id)} disabled ={isReadOnly()} />,
          <GridActionsCellItem icon={<Delete />} label="Delete" onClick={() => handleDeleteClick(id)} disabled ={isReadOnly()} />,
        ];
      },
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box style={{ height: 'calc(100vh - 280px)', width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          {/* Left side - Selection info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {selectedRows.size > 0 ? `${selectedRows.size} row(s) selected` : 'No rows selected'}
            </Typography>
          </Box>

          {/* Right side - Save All and Add buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {hasUnsavedRows && (
                <Button 
                    color="success" 
                    onClick={handleSaveAll}
                    sx={{ 
                    backgroundColor: '#e8f5e8',
                    '&:hover': {
                        backgroundColor: '#d4f2d4',
                    }
                    }}
                >
                    <Save sx={{ fontSize: 16, mr: 1 }} />
                    <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                    Save All ({getUnsavedRows().length})
                    </Typography>
                </Button>
                )}
            
            <IconButton color="primary" onClick={handleAddRow} disabled ={isReadOnly()}>
              <Typography sx={{ fontSize: "16px" }}>Add New Rows</Typography> 
              <Add sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>
        
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          editMode="row"
          rowModesModel={rowModesModel}
          onRowModesModelChange={setRowModesModel}
          onRowEditStop={handleRowEditStop}
          processRowUpdate={processRowUpdate}
          disableColumnMenu
          disableRowSelectionOnClick
          scrollbarSize={17}
          slots={{ pagination: CustomPagination }}
          sx={{
            '& .MuiDataGrid-columnHeader': {
              fontWeight: 600,
              backgroundColor: '#f16253',
              color: '#fff',
            },
            "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 600,
                width: "100%",
              },
            // Additional styling for pinned columns
            '& .MuiDataGrid-columnHeader--pinned': {
              backgroundColor: '#e53e30',
            },
            '& .MuiDataGrid-cell--pinned': {
              backgroundColor: '#fafafa',
            },
            // Highlight selected rows
            '& .MuiDataGrid-row': {
              '&.Mui-selected': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.12)',
                },
              },
              // Highlight new/unsaved rows
              '&.unsaved-row': {
                backgroundColor: 'rgba(255, 193, 7, 0.1)',
                borderLeft: '4px solid #ffc107',
                '&:hover': {
                  backgroundColor: 'rgba(255, 193, 7, 0.15)',
                },
              },
              // Highlight editing rows
              '&.editing-row': {
                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                borderLeft: '4px solid #1976d2',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.15)',
                },
              },
            },
            // Ensure horizontal scroll is visible and styled
            '& .MuiDataGrid-virtualScroller': {
              overflowX: 'auto',
            },
            '& .MuiDataGrid-virtualScrollerContent': {
              minWidth: 'max-content',
            },
            // Custom scrollbar styling
            '& ::-webkit-scrollbar': {
              height: 12,
            },
            '& ::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
            },
            '& ::-webkit-scrollbar-thumb': {
              backgroundColor: '#c1c1c1',
              borderRadius: 6,
            },
            '& ::-webkit-scrollbar-thumb:hover': {
              backgroundColor: '#a8a8a8',
            },
          }}
          // Apply custom classes to rows based on their state
          getRowClassName={(params) => {
            if (rowModesModel[params.id]?.mode === GridRowModes.Edit) {
              return 'editing-row';
            }
            if (params.row.isNew) {
              return 'unsaved-row';
            }
            return '';
          }}
        />
        
        {/* Debug: Show selected rows and unsaved rows */}
        {(selectedRows.size > 0 || hasUnsavedRows) && (
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            {selectedRows.size > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Selected Row IDs:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Array.from(selectedRows).join(', ')}
                </Typography>
              </Box>
            )}
            
            {hasUnsavedRows && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Unsaved Rows ({getUnsavedRows().length}):
                </Typography>
                <Typography variant="body2" color="warning.main">
                  {getUnsavedRows().map(row => 
                    `Row ${row.id} ${row.isNew ? '(New)' : '(Editing)'}`
                  ).join(', ')}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default EditableDataGrid;